import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      mainGradient: string;
      selectedMainGradient: string;
      border: string;
      placeholder: string;
      lightGray: string;
      contentBackground: string;
      iconBackground: string;
      iconColor: string;
      deleteIconColor: string;
    };
    status: {
      completed: { color: string; background: string };
      pending: { color: string; background: string };
      active: { color: string; background: string };
      inactive: { color: string; background: string };
      ordered: { color: string; background: string };
      repair: { color: string; background: string };
    };
  }

  interface PaletteOptions {
    custom?: Partial<Palette['custom']>;
    status?: Partial<Palette['status']>;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: '#2E77D0',
      light: '#EBF3FA',
      dark: '#133359',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#EBF3FA',
    },
    background: {
      default: '#f4f5f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#344054',
      secondary: '#667085',
      disabled: '#ACACAC',
    },
    error: {
      main: '#f44336',
      light: '#FEF2F1',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      contrastText: '#ffffff',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    common: {
      white: '#ffffff',
      black: '#242424',
    },
    custom: {
      mainGradient: 'linear-gradient(180deg, #FFFFFF 0%, #E4EFF7 100%)',
      selectedMainGradient: 'linear-gradient(90deg, #296AAB 0%, #3283CD 100%)',
      border: '#9C9AA5',
      placeholder: '#9C9AA5',
      lightGray: '#F2F4F7',
      contentBackground: '#F6F6F6',
      iconBackground: '#F2F7FC',
      iconColor: '#919DB8',
      deleteIconColor: '#D1D3D7',
    },
    status: {
      completed: { color: '#027A48', background: '#ECFDF3' },
      pending: { color: '#667085', background: '#FFED51' },
      active: { color: '#69C832', background: '#F0FAEB' },
      inactive: { color: '#F19D03', background: '#FEF9E6' },
      ordered: { color: '#2E77D0', background: '#EBF3FA' },
      repair: { color: '#F03D3D', background: '#FEECEC' },
    },
  },
  typography: {
    fontFamily: 'Outfit, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    button: {
      fontWeight: 600,
      textTransform: 'capitalize',
    },
    h1: {
      fontSize: '3.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '40px',
      fontWeight: 600,
    },
    h3: {
      fontSize: '32px',
      fontWeight: 600,
    },
    h4: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '24px',
      fontWeight: 600,
    },
    h6: {
      fontSize: '22px',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '20px',
    },
    subtitle2: {
      fontSize: '14px',
    },
    body1: {
      fontSize: '18px',
    },
    body2: {
      fontSize: '16px',
    },
    overline: {
      fontSize: '12px',
      textTransform: 'none',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '8px',
          fontWeight: 600,
          textTransform: 'capitalize',
          '&.Mui-disabled': {
            backgroundColor: theme.palette.custom.lightGray,
            color: theme.palette.text.disabled,
          },
        }),
        contained: {
          borderRadius: '0.5rem',
        },
        containedPrimary: ({ theme }) => ({
          background: theme.palette.custom.selectedMainGradient,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            background: theme.palette.primary.dark,
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          borderRadius: '0.5rem',
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: theme.palette.primary.light,
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '14px',
          boxShadow: '0px 2px 16px 0px #1E315F1A',
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: ({ theme }) => ({
          '&::placeholder': {
            color: theme.palette.custom.placeholder,
            opacity: 1,
          },
        }),
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.custom.border,
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }) => ({
          '&::placeholder': {
            color: theme.palette.custom.placeholder,
            opacity: 1,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontWeight: 'normal',
          '&.Mui-selected': {
            fontWeight: 'bold',
            color: theme.palette.primary.main,
          },
        }),
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
});

